/**
 * TrainerRoster — New Clients Tab Component
 *
 * Shows the trainer's client roster (active + pending invites).
 * Tapping an active client card calls enterClientView() to switch
 * the entire app into that client's data context.
 *
 * Also provides the "Invite Client" flow that generates a shareable link.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useClientContext } from "@/contexts/ClientContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UserPlus,
  Users,
  Clock,
  CheckCircle2,
  Copy,
  ChevronRight,
  Dumbbell,
  CalendarDays,
  XCircle,
} from "lucide-react";

export default function TrainerRoster() {
  const { enterClientView } = useClientContext();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [contractStart, setContractStart] = useState("");
  const [contractEnd, setContractEnd] = useState("");
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  const { data: roster, isLoading, refetch } = trpc.trainerPortal.getRoster.useQuery();

  const createInviteMutation = trpc.trainerPortal.createInvite.useMutation({
    onSuccess: (data) => {
      setGeneratedLink(data.inviteUrl);
      refetch();
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to create invite.");
    },
  });

  const deactivateMutation = trpc.trainerPortal.deactivateLink.useMutation({
    onSuccess: () => {
      toast.success("Client removed from roster.");
      refetch();
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to remove client.");
    },
  });

  const handleCreateInvite = () => {
    createInviteMutation.mutate({
      inviteEmail: inviteEmail || undefined,
      contractStartDate: contractStart || undefined,
      contractEndDate: contractEnd || undefined,
      origin: window.location.origin,
    });
  };

  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link).then(() => {
      toast.success("Invite link copied to clipboard!");
    });
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setGeneratedLink(null);
    setInviteEmail("");
    setContractStart("");
    setContractEnd("");
  };

  const activeClients = roster?.filter((c) => c.status === "active") ?? [];
  const pendingInvites = roster?.filter((c) => c.status === "pending") ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="font-mono text-base font-bold uppercase tracking-wider text-foreground">
            CLIENT ROSTER
          </h2>
          {activeClients.length > 0 && (
            <span className="font-mono text-xs text-muted-foreground">
              ({activeClients.length} active)
            </span>
          )}
        </div>

        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              className="font-mono text-xs uppercase tracking-wider gap-1.5"
            >
              <UserPlus className="h-3.5 w-3.5" />
              INVITE CLIENT
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-mono uppercase tracking-wider text-sm">
                Invite New Client
              </DialogTitle>
            </DialogHeader>

            {!generatedLink ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Client Email (optional)
                  </Label>
                  <Input
                    type="email"
                    placeholder="client@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Contract Start
                    </Label>
                    <Input
                      type="date"
                      value={contractStart}
                      onChange={(e) => setContractStart(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Contract End
                    </Label>
                    <Input
                      type="date"
                      value={contractEnd}
                      onChange={(e) => setContractEnd(e.target.value)}
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 font-mono text-xs"
                    onClick={handleCloseInviteDialog}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1 font-mono text-xs uppercase tracking-wider"
                    onClick={handleCreateInvite}
                    disabled={createInviteMutation.isPending}
                  >
                    {createInviteMutation.isPending ? "Generating..." : "Generate Link"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  <span className="font-mono text-xs uppercase tracking-wider">
                    Invite link created!
                  </span>
                </div>
                <div className="bg-secondary/50 rounded border border-border p-3 space-y-2">
                  <p className="font-mono text-[11px] text-muted-foreground break-all">
                    {generatedLink}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full font-mono text-xs uppercase tracking-wider gap-1.5"
                    onClick={() => handleCopyLink(generatedLink)}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy Link
                  </Button>
                </div>
                <p className="font-mono text-[10px] text-muted-foreground">
                  Share this link with your client. When they sign in and visit the link, they'll be connected to your roster automatically.
                </p>
                <Button
                  size="sm"
                  className="w-full font-mono text-xs uppercase tracking-wider"
                  onClick={handleCloseInviteDialog}
                >
                  Done
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 bg-secondary/30 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activeClients.length === 0 && pendingInvites.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
          <div className="h-16 w-16 bg-secondary/50 rounded-full flex items-center justify-center">
            <Users className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <p className="font-mono text-sm font-bold uppercase tracking-wider text-foreground">
              No Clients Yet
            </p>
            <p className="font-mono text-xs text-muted-foreground mt-1">
              Invite your first client to get started.
            </p>
          </div>
          <Button
            size="sm"
            className="font-mono text-xs uppercase tracking-wider gap-1.5"
            onClick={() => setInviteDialogOpen(true)}
          >
            <UserPlus className="h-3.5 w-3.5" />
            Invite Your First Client
          </Button>
        </div>
      )}

      {/* Active Clients */}
      {activeClients.length > 0 && (
        <div className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Active Clients
          </p>
          {activeClients.map((client) => (
            <button
              key={client.linkId}
              onClick={() =>
                enterClientView({
                  clientId: client.clientId,
                  clientName: client.clientName ?? `Client #${client.clientId}`,
                })
              }
              className="w-full text-left bg-card border border-border hover:border-primary/50 hover:bg-primary/5 rounded-lg p-4 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-primary/20 rounded-full flex items-center justify-center shrink-0">
                    <span className="font-mono text-sm font-bold text-primary">
                      {(client.clientName ?? "?")[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-mono text-sm font-bold text-foreground">
                      {client.clientName ?? `Client #${client.clientId}`}
                    </p>
                    {client.clientEmail && (
                      <p className="font-mono text-[11px] text-muted-foreground">
                        {client.clientEmail}
                      </p>
                    )}
                    {(client.contractStartDate || client.contractEndDate) && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <CalendarDays className="h-3 w-3 text-muted-foreground" />
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {client.contractStartDate ?? "?"} → {client.contractEndDate ?? "ongoing"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-primary group-hover:text-primary/80 hidden sm:block">
                    View Data
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="space-y-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Pending Invites
          </p>
          {pendingInvites.map((invite) => (
            <div
              key={invite.linkId}
              className="bg-card/50 border border-border/50 rounded-lg p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-amber-500/10 rounded-full flex items-center justify-center shrink-0">
                    <Clock className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="font-mono text-sm font-bold text-muted-foreground">
                      {invite.clientEmail ?? "Invite Pending"}
                    </p>
                    <p className="font-mono text-[10px] text-amber-400/70 uppercase tracking-wider">
                      Awaiting client sign-up
                    </p>
                    {invite.inviteCode && (
                      <button
                        onClick={() =>
                          handleCopyLink(
                            `${window.location.origin}/join/${invite.inviteCode}`
                          )
                        }
                        className="flex items-center gap-1 mt-1 font-mono text-[10px] text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Copy className="h-3 w-3" />
                        Copy invite link
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active clients: remove option */}
      {activeClients.length > 0 && (
        <div className="pt-2 border-t border-border/50">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">
            Manage
          </p>
          <div className="space-y-2">
            {activeClients.map((client) => (
              <div key={client.linkId} className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2">
                  <Dumbbell className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-mono text-xs text-muted-foreground">
                    {client.clientName ?? `Client #${client.clientId}`}
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Remove ${client.clientName ?? "this client"} from your roster?`)) {
                      deactivateMutation.mutate({ clientId: client.clientId });
                    }
                  }}
                  className="flex items-center gap-1 font-mono text-[10px] text-red-400/60 hover:text-red-400 transition-colors"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
